import React from 'react';
import { Layout, Row, Col, Card as AntCard, Typography as AntTypography } from 'antd';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Registro } from '../types';
import DateFilter from '../components/DateFilter';
import { useFilteredRegistros } from '../hooks/useFilteredRegistros';
import { useRondasData } from '../hooks/useRondasData';
import { categorizarIncidencia, esSinNovedad } from '../utils/incidenciasUtils';

const { Content } = Layout;
const { Title, Text } = AntTypography;

interface GraficosProps {
  registros: Registro[];
}

// Paleta de 13 colores bien diferenciados
const COLORS = [
  '#0088FE', // Azul brillante
  '#FFBB28', // Amarillo dorado
  '#FF8042', // Naranja fuerte
  '#00C49F', // Verde agua
  '#FF4F81', // Rosa fuerte
  '#A28FD0', // Lila / violeta
  '#FF6F00', // Naranja oscuro
  '#4DD0E1', // Celeste
  '#AF19FF', // Púrpura intenso
  '#B0DE09', // Verde lima
  '#FFB300', // Amarillo intenso
  '#E6194B', // Rojo vibrante
  '#3CB44B', // Verde clásico
];

const Graficos: React.FC<GraficosProps> = ({ registros }) => {
  // Estado inicial: periodo '7d' y rango acorde
  const [periodo, setPeriodo] = React.useState('7d');
  const [dateRange, setDateRange] = React.useState<{ startDate: string; endDate: string }>(() => {
    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - 6); // 7 días incluyendo hoy
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
    };
  });

  // Hook para filtrar registros según el periodo y rango
  const {
    dateRange: filtroDateRange,
    setDateRange: setFiltroDateRange,
    periodo: filtroPeriodo,
    setPeriodo: setFiltroPeriodo,
    registrosFiltrados,
  } = useFilteredRegistros(registros, periodo, dateRange, 'periodo');

  // Sincroniza el estado local con el hook para mantener controlado el filtro
  React.useEffect(() => {
    setPeriodo(filtroPeriodo);
    setDateRange(filtroDateRange);
  }, [filtroPeriodo, filtroDateRange]);

  // Función para determinar el turno según la hora
  const getTurno = (fecha: string): 'Día' | 'Noche' => {
    const hora = new Date(fecha).getHours();
    return hora >= 6 && hora < 18 ? 'Día' : 'Noche';
  };

  // Datos para el gráfico de incidencias por tipo (categorizado)
  const getIncidenciasPorTipo = () => {
    const incidencias = registrosFiltrados
      .filter(r => !esSinNovedad(r.incidencia))
      .reduce((acc: { [key: string]: number }, registro) => {
        const categoria = categorizarIncidencia(registro.incidencia || '');
        if (!categoria) return acc; // Ignora incidencias no clasificadas
        acc[categoria] = (acc[categoria] || 0) + 1;
        return acc;
      }, {});

    return Object.entries(incidencias)
      .map(([tipo, cantidad]) => ({
        tipo,
        cantidad,
      }))
      .sort((a, b) => b.cantidad - a.cantidad);
  };

  // Datos para el gráfico de incidencias por tipo y turno (por categoría, top 5)
  const getIncidenciasPorTipoYTurno = () => {
    // Agrupa por categoría general definida en categorizarIncidencia
    const incidencias = registrosFiltrados
      .filter(r => !esSinNovedad(r.incidencia))
      .reduce((acc: { [key: string]: { dia: number; noche: number } }, registro) => {
        const categoria = categorizarIncidencia(registro.incidencia || '');
        if (!categoria) return acc; // Ignora incidencias no clasificadas
        const turno = getTurno(registro.fecha);
        if (!acc[categoria]) {
          acc[categoria] = { dia: 0, noche: 0 };
        }
        if (turno === 'Día') {
          acc[categoria].dia++;
        } else {
          acc[categoria].noche++;
        }
        return acc;
      }, {});

    return Object.entries(incidencias)
      .sort((a, b) => b[1].dia + b[1].noche - (a[1].dia + a[1].noche))
      .slice(0, 5)
      .map(([tipo, datos]) => ({
        tipo,
        'Turno Día': datos.dia,
        'Turno Noche': datos.noche,
      }));
  };

  // Datos para el gráfico de tendencia semanal
  const getTendenciaSemanal = () => {
    const incidenciasPorSemana = registrosFiltrados
      .filter(r => !esSinNovedad(r.incidencia))
      .reduce((acc: { [key: string]: { [key: string]: number } }, registro) => {
        const fecha = new Date(registro.fecha);
        const inicioSemana = new Date(fecha);
        inicioSemana.setDate(fecha.getDate() - fecha.getDay());
        const semana = inicioSemana.toLocaleDateString();
        const tipo = categorizarIncidencia(registro.incidencia || '') || 'Otros';
        if (!acc[semana]) {
          acc[semana] = {};
        }
        acc[semana][tipo] = (acc[semana][tipo] || 0) + 1;
        return acc;
      }, {});

    return Object.entries(incidenciasPorSemana)
      .map(([semana, tipos]) => ({
        semana,
        ...tipos,
        total: Object.values(tipos).reduce((sum, val) => sum + val, 0),
      }))
      .sort((a, b) => new Date(a.semana).getTime() - new Date(b.semana).getTime());
  };

  // --- NUEVO: PieChart de incidencias por lugar ---
  const getIncidenciasPorLugar = () => {
    const incidencias = registrosFiltrados
      .filter(r => !esSinNovedad(r.incidencia))
      .reduce((acc: { [key: string]: number }, registro) => {
        const lugar = registro.lugar || 'Sin especificar';
        acc[lugar] = (acc[lugar] || 0) + 1;
        return acc;
      }, {});
    return Object.entries(incidencias)
      .map(([lugar, cantidad]) => ({ lugar, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad);
  };

  // Opciones de periodo SOLO para gráficos (sin 24 horas)
  const periodOptions = [
    { value: '7d', label: 'Últimos 7 días' },
    { value: '15d', label: 'Últimos 15 días' },
    { value: '30d', label: 'Últimos 30 días' },
    { value: 'Personalizado', label: 'Rango personalizado' },
  ];

  // --- Top incidencias para Pareto ---
  const getParetoIncidencias = () => {
    const data = getIncidenciasPorTipo().slice(0, 10); // Top 10 para mejor visual
    let acumulado = 0;
    const total = data.reduce((sum, d) => sum + d.cantidad, 0);
    return data.map(d => {
      acumulado += d.cantidad;
      return {
        ...d,
        porcentaje: Number(((acumulado / total) * 100).toFixed(2)),
      };
    });
  };

  // Obtener el nombre del cliente
  const { cliente } = useRondasData(periodo);

  return (
    <Layout
      style={{ minHeight: '100vh', background: '#f0f2f5', borderRadius: 12, overflow: 'hidden' }}
    >
      <Content style={{ margin: '24px 16px 0', borderRadius: 12, background: '#f0f2f5' }}>
        {cliente && (
          <AntTypography.Title
            level={2}
            style={{
              fontWeight: 'bold',
              margin: 0,
              background: 'rgb(24, 144, 255)',
              padding: '4px 12px',
              borderRadius: 12,
              color: 'white',
              marginBottom: 24,
            }}
          >
            {cliente}
          </AntTypography.Title>
        )}
        <div style={{ padding: 24, minHeight: 360, background: '#fff', borderRadius: 12 }}>
          <DateFilter
            registros={registros}
            registrosFiltrados={registrosFiltrados}
            periodo={periodo}
            setPeriodo={setFiltroPeriodo}
            dateRange={dateRange}
            setRangeStart={date =>
              setFiltroDateRange({ ...dateRange, startDate: date.format('YYYY-MM-DD') })
            }
            setRangeEnd={date =>
              setFiltroDateRange({ ...dateRange, endDate: date.format('YYYY-MM-DD') })
            }
            onDateChange={(start, end) =>
              setFiltroDateRange({
                startDate: start.format('YYYY-MM-DD'),
                endDate: end.format('YYYY-MM-DD'),
              })
            }
            periodOptions={periodOptions}
          />

          {/* <Title level={2} style={{ marginBottom: 32 }}>Gráficos y Estadísticas</Title> */}

          <Row gutter={[24, 24]}>
            {/* Tarjetas de resumen */}
            <Col xs={24} md={8}>
              <AntCard bordered style={{ borderRadius: 12 }}>
                <Text type="secondary">Total de Registros</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {registrosFiltrados.length}
                </Title>
              </AntCard>
            </Col>
            <Col xs={24} md={8}>
              <AntCard bordered style={{ borderRadius: 12 }}>
                <Text type="secondary">Incidencias Reportadas</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {registrosFiltrados.filter(r => !esSinNovedad(r.incidencia)).length}
                </Title>
              </AntCard>
            </Col>
            <Col xs={24} md={8}>
              <AntCard bordered style={{ borderRadius: 12 }}>
                <Text type="secondary">Lugares Monitoreados</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {new Set(registrosFiltrados.map(r => r.lugar)).size}
                </Title>
              </AntCard>
            </Col>

            {/* Top Incidencias (Gráfico de Pareto) */}
            <Col xs={24} md={12}>
              <AntCard bordered style={{ borderRadius: 12, height: 420 }}>
                <Title level={4} style={{ marginBottom: 16 }}>
                  Incidencias más frecuentes
                </Title>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={getParetoIncidencias()}
                    margin={{ top: 16, right: 16, left: 32, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="tipo" />
                    <YAxis yAxisId="left" allowDecimals={false} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 100]}
                      tickFormatter={v => `${v}%`}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name === 'porcentaje' ? `${value}% acumulado` : value
                      }
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="cantidad"
                      fill="#8884d8"
                      barSize={32}
                      name="Cantidad"
                    />
                    <LineChart data={getParetoIncidencias()}>
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="porcentaje"
                        stroke="#FF8042"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="% Acumulado"
                      />
                    </LineChart>
                  </BarChart>
                </ResponsiveContainer>
              </AntCard>
            </Col>

            {/* Incidencias por Tipo y Turno */}
            <Col xs={24} md={12}>
              <AntCard bordered style={{ borderRadius: 12, height: 420 }}>
                <Title level={4} style={{ marginBottom: 16 }}>
                  Incidencias por Tipo y Turno
                </Title>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={getIncidenciasPorTipoYTurno()}
                    layout="vertical"
                    margin={{ top: 16, right: 16, left: 32, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="tipo" width={120} />
                    <Tooltip />
                    <Bar dataKey="Turno Día" fill="#82ca9d" barSize={20} />
                    <Bar dataKey="Turno Noche" fill="#8884d8" barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </AntCard>
            </Col>

            {/* PieChart Incidencias por Lugar (ancho completo, leyenda a la izquierda) */}
            <Col xs={24}>
              <AntCard bordered style={{ borderRadius: 12, height: 440 }}>
                <Title level={4} style={{ marginBottom: 16 }}>
                  Distribución de Incidencias por Lugar
                </Title>
                <div style={{ display: 'flex', alignItems: 'center', height: 340 }}>
                  {/* Leyenda personalizada más compacta a la izquierda */}
                  <div
                    style={{
                      minWidth: 200,
                      maxWidth: 240,
                      marginRight: 24,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 8,
                    }}
                  >
                    {getIncidenciasPorLugar().map((entry, index) => (
                      <div
                        key={`legend-lugar-${index}`}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            background: COLORS[index % COLORS.length],
                            marginRight: 10,
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontWeight: 600, fontSize: 14, marginRight: 6 }}>
                          {entry.cantidad}
                        </span>
                        <span style={{ fontWeight: 500, fontSize: 13, color: '#444' }}>
                          {entry.lugar}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{ flex: 1, height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getIncidenciasPorLugar()}
                          cx="55%"
                          cy="50%"
                          labelLine={false}
                          label={({ cantidad }) => cantidad}
                          outerRadius={150}
                          fill="#8884d8"
                          dataKey="cantidad"
                          nameKey="lugar"
                        >
                          {getIncidenciasPorLugar().map((entry, index) => (
                            <Cell
                              key={`cell-lugar-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </AntCard>
            </Col>

            {/* Tendencia Semanal (Gráfico de Líneas) */}
            <Col xs={24}>
              <AntCard bordered style={{ borderRadius: 12, height: 440 }}>
                <Title level={4} style={{ marginBottom: 16 }}>
                  Tendencia Semanal de Incidencias
                </Title>
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart
                    data={getTendenciaSemanal()}
                    margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="semana" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#8884d8" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </AntCard>
            </Col>
          </Row>
        </div>
      </Content>
    </Layout>
  );
};

export default Graficos;
